/**
 * useCaptureCamera — the still-capture camera machine for the capture step
 * (S7-6 droom self-capture). Follows the useMediaPermissions conventions:
 * primer BEFORE the native prompt, honest denied-recovery copy (no site can
 * re-open a denied prompt), webview detection — sharing the SAME detectors and
 * recovery copy via the exported helpers.
 *
 * Lifecycle correctness (the part that bites in the field):
 *   • start() always stops the previous stream BEFORE re-acquiring (facing
 *     switches never leave a zombie track holding the camera).
 *   • All tracks stop on unmount.
 *   • A stale getUserMedia resolution (rapid facing switch / unmount race) is
 *     detected by a sequence counter and its tracks are stopped immediately.
 *   • The <video> element attaches via a CALLBACK ref, so remounts (viewfinder
 *     ↔ preview swaps) re-attach the live stream without effect gymnastics.
 *
 * captureFrame() draws the current frame at the video track's NATIVE
 * resolution → JPEG (quality 0.92). No frames are logged or persisted here —
 * the caller owns the blob's lifetime (PII discipline).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  detectBrowser,
  detectWebview,
  queryPermissionState,
  recoveryFor,
  type BrowserFamily,
} from './useMediaPermissions';

export type CaptureFacing = 'environment' | 'user';

export type CaptureCameraPhase =
  | 'checking' // querying the Permissions API on entry
  | 'primer' // explainer shown; the camera starts from the user's tap
  | 'starting' // getUserMedia in flight (the native prompt may be showing)
  | 'live' // viewfinder running
  | 'denied' // denied in-browser — guided recovery, honest retry
  | 'os-denied' // the prompt never appeared — system-level block
  | 'no-device' // no usable camera on this device
  | 'webview' // in-app webview — must open in a real browser
  | 'unsupported'; // getUserMedia unavailable

const JPEG_QUALITY = 0.92;

export interface CaptureCameraApi {
  readonly phase: CaptureCameraPhase;
  readonly browser: BrowserFamily;
  /** True when the browser already granted camera — the primer can be skipped. */
  readonly preGranted: boolean;
  /** The facing requested by the last start() call. */
  readonly requestedFacing: CaptureFacing | null;
  /** The facing the track actually reports ('user'/'environment'), when known. */
  readonly activeFacing: CaptureFacing | null;
  /** Calm, user-facing detail line (never a stack trace). */
  readonly detail: string | null;
  /** Browser-specific recovery steps for denied / os-denied. */
  readonly recoverySteps: readonly string[];
  /** Callback ref for the <video> viewfinder (safe across remounts). */
  readonly attachVideo: (el: HTMLVideoElement | null) => void;
  /**
   * The current live capture stream, or null when not live. Read-only observation seam for the
   * capture-integrity fingerprint (S5-4) — the caller must NOT stop these tracks (this hook owns them).
   */
  readonly getStream: () => MediaStream | null;
  /** Acquire the camera for a sub-capture; stops any previous stream first. */
  readonly start: (facing: CaptureFacing) => void;
  /** Stop all tracks (also runs automatically on unmount). */
  readonly stop: () => void;
  /** Grab the current frame at native track resolution as a JPEG blob. */
  readonly captureFrame: () => Promise<Blob | null>;
}

export function useCaptureCamera(enabled: boolean): CaptureCameraApi {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const browser = detectBrowser(ua);
  const isWebview = detectWebview(ua);

  const [phase, setPhase] = useState<CaptureCameraPhase>('checking');
  const [preGranted, setPreGranted] = useState(false);
  const [requestedFacing, setRequestedFacing] = useState<CaptureFacing | null>(null);
  const [activeFacing, setActiveFacing] = useState<CaptureFacing | null>(null);
  const [detail, setDetail] = useState<string | null>(null);

  const disposed = useRef(false);
  const streamRef = useRef<MediaStream | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  /** Monotonic start() sequence — stale acquisitions stop their own tracks. */
  const startSeq = useRef(0);

  const stopTracks = useCallback(() => {
    const s = streamRef.current;
    if (s) for (const t of s.getTracks()) t.stop();
    streamRef.current = null;
    const el = videoElRef.current;
    if (el) el.srcObject = null;
  }, []);

  // Unmount: invalidate any in-flight start and release the camera.
  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
      startSeq.current += 1;
      stopTracks();
    };
  }, [stopTracks]);

  // Entry: webview / support / permission pre-check (no prompt fired here).
  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setPhase('unsupported');
      return;
    }
    if (isWebview) {
      setPhase('webview');
      return;
    }
    let cancelled = false;
    (async () => {
      const cam = await queryPermissionState('camera');
      if (cancelled || disposed.current) return;
      if (cam === 'denied') {
        // Already denied — an Allow tap would silently fail; go straight to
        // the honest recovery steps instead of a prompt that cannot appear.
        setPhase('denied');
        return;
      }
      setPreGranted(cam === 'granted');
      setPhase('primer');
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, isWebview]);

  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    videoElRef.current = el;
    if (el && streamRef.current) {
      el.srcObject = streamRef.current;
      void el.play().catch(() => {
        /* autoplay policies — playsInline+muted make this a non-issue in practice */
      });
    }
  }, []);

  const start = useCallback(
    async (facing: CaptureFacing): Promise<void> => {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        setPhase('unsupported');
        return;
      }
      const seq = ++startSeq.current;
      stopTracks(); // release BEFORE re-acquiring — never hold two streams
      setRequestedFacing(facing);
      setActiveFacing(null);
      setDetail(null);
      setPhase('starting');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
        if (seq !== startSeq.current || disposed.current) {
          // A newer start()/unmount superseded this acquisition — release it.
          for (const t of stream.getTracks()) t.stop();
          return;
        }
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        const reported = track?.getSettings().facingMode;
        setActiveFacing(
          reported === 'user' || reported === 'environment' ? reported : null,
        );
        const el = videoElRef.current;
        if (el) {
          el.srcObject = stream;
          void el.play().catch(() => {});
        }
        setPhase('live');
      } catch (e) {
        if (seq !== startSeq.current || disposed.current) return;
        const name = e instanceof DOMException ? e.name : '';
        if (
          name === 'NotFoundError' ||
          name === 'DevicesNotFoundError' ||
          name === 'OverconstrainedError'
        ) {
          setDetail('droom.cam.nodevice.hookDetail');
          setPhase('no-device');
          return;
        }
        // NotAllowedError/SecurityError: distinguish an in-browser denial from
        // an OS-level block (same probe convention as useMediaPermissions —
        // 'prompt' after a rejection means the system swallowed the prompt).
        const after = await queryPermissionState('camera');
        if (seq !== startSeq.current || disposed.current) return;
        setPhase(after === 'prompt' || after === null ? 'os-denied' : 'denied');
      }
    },
    [stopTracks],
  );

  /** Stable void wrapper — safe to use in consumer effect dependency arrays. */
  const startStable = useCallback(
    (facing: CaptureFacing): void => {
      void start(facing);
    },
    [start],
  );

  const stop = useCallback(() => {
    startSeq.current += 1; // invalidate any in-flight acquisition
    stopTracks();
    setActiveFacing(null);
  }, [stopTracks]);

  /** Read-only accessor to the live stream for the capture-integrity collector (never stops the tracks). */
  const getStream = useCallback((): MediaStream | null => streamRef.current, []);

  const captureFrame = useCallback(async (): Promise<Blob | null> => {
    const el = videoElRef.current;
    if (!el || el.readyState < 2) return null; // < HAVE_CURRENT_DATA — no frame yet
    const w = el.videoWidth;
    const h = el.videoHeight;
    if (!w || !h) return null;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // Draw the RAW sensor frame (never the CSS-mirrored preview) at native size.
    ctx.drawImage(el, 0, 0, w, h);
    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY);
    });
  }, []);

  return {
    phase,
    browser,
    preGranted,
    requestedFacing,
    activeFacing,
    detail,
    recoverySteps:
      phase === 'os-denied' ? recoveryFor(browser, true) : recoveryFor(browser, false),
    attachVideo,
    getStream,
    start: startStable,
    stop,
    captureFrame,
  };
}
